import { useBranchContext } from 'providers/BranchProvider';
import { useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axiosInterceptors';
import DataTable from '../BranchProductInventory/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { TPurchaseOrder } from 'utils/types';
import { Button, Form, Row } from 'react-bootstrap';
import CustomModal, {
  CustomModalProps,
  ModalHandle
} from 'components/modals/CustomModal';
import CreatePurchaseOrderForm, {
  PurchaseOrderHandle
} from './CreatePurchaseOrderForm';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';
import { AxiosError } from 'axios';
import BadgeStatus from 'utils/BadgeStatus';
import ProgressBadge from 'components/common/ProgressBadge';
import useFetchSupplier from '../SupplierPayment/useFetchSupplier';
import { formatLongIsoDateOrDash } from 'utils/formatLongIsoDate';
import { formatNumberWithCommas } from 'utils/formats';
import { useAuth } from 'hooks/useAuth';
import { FeatureRoles, hasAnyRole } from 'utils/roleAccess';
import ProcurementLimitWarning from '../ProcurementLimitWarning';
import SupplierName from './SupplierName';

const COMBINABLE_PURCHASE_ORDER_STATUSES = new Set([
  'New',
  'Future',
  'AwaitingApproval',
  'Approved'
]);

const canCombinePurchaseOrder = (order: TPurchaseOrder) =>
  !order.isPending &&
  !!order.supplierId &&
  COMBINABLE_PURCHASE_ORDER_STATUSES.has(order.status);

const PurchaseOrder = () => {
  const { selectedBranch } = useBranchContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  const canDelete = hasAnyRole(token?.roles, FeatureRoles.purchaseOrderDelete);
  const canCloseOld = hasAnyRole(token?.roles, FeatureRoles.purchaseOrderClose);
  const isAdmin = hasAnyRole(token?.roles, FeatureRoles.adminOnly);
  const [isClosingOld, setIsClosingOld] = useState(false);
  const [isCombining, setIsCombining] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<
    Map<number, TPurchaseOrder>
  >(() => new Map());
  const selectedOrdersRef = useRef(selectedOrders);
  selectedOrdersRef.current = selectedOrders;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [modalProps, setModalProps] = useState<CustomModalProps>();
  const modalRef = useRef<ModalHandle>({
    show: () => {},
    hide: () => {}
  });
  const addPurchaseOrderFormRef = useRef<PurchaseOrderHandle>(null);
  const { suppliers } = useFetchSupplier();
  const [filters, setFilters] = useState({
    supplierId: undefined,
    status: undefined
  });

  const purchaseOrderStatus = [
    { value: 'New', label: 'New' },
    { value: 'Future', label: 'Future' },
    { value: 'Analyzing', label: 'Analyzing' },
    { value: 'ForDeletion', label: 'For Deletion' },
    { value: 'AwaitingApproval', label: 'Awaiting Approval' },
    { value: 'ForConsolidation', label: 'For Consolidation' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Sent', label: 'Sent' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    if (!selectedBranch?.branchDomain) {
      throw new Error('Branch domain not set');
    }
    const { data } = await axiosInstance.get(
      `/api/v1/${selectedBranch?.branchDomain as string}/purchaseOrders/`,
      {
        params: {
          page,
          pageSize,
          search,
          orderBy: orderBy ? orderBy : 'createdOn',
          orderByDesc: orderByDesc ? false : true,
          supplierId: filters.supplierId,
          ...(filters.status === 'ForConsolidation'
            ? { needsConsolidation: true }
            : { status: filters.status })
        }
      }
    );
    return data;
  };

  const canRequestDeletion = (status: string) =>
    status === 'New' ||
    status === 'Future' ||
    status === 'Analyzing' ||
    status === 'AwaitingApproval';

  const toggleSelected = (order: TPurchaseOrder, checked: boolean) => {
    setSelectedOrders(prev => {
      const next = new Map(prev);
      if (checked) {
        next.set(order.purchaseOrderId, order);
      } else {
        next.delete(order.purchaseOrderId);
      }
      return next;
    });
  };

  const columns: ColumnDef<TPurchaseOrder>[] = [
    {
      id: 'select',
      header: '',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const combinable = canCombinePurchaseOrder(original);
        return (
          <Form.Check
            type="checkbox"
            checked={selectedOrders.has(original.purchaseOrderId)}
            disabled={!combinable}
            title={
              combinable
                ? `Select purchase order ${original.purchaseOrderId}`
                : 'Only new, future, awaiting approval, or approved purchase orders from the same supplier can be combined'
            }
            onChange={e => toggleSelected(original, e.target.checked)}
            onClick={e => e.stopPropagation()}
            aria-label={`Select purchase order ${original.purchaseOrderId}`}
          />
        );
      }
    },
    {
      id: 'purchaseOrderId',
      accessorKey: 'purchaseOrderId',
      header: 'Purchase Order ID',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const to =
          original.isPending || !original.supplierId
            ? '/purchase-order/pending'
            : `/purchase-order-details/${original.purchaseOrderId}`;
        return (
          <>
            <Link to={to}>{original.purchaseOrderId}</Link>
          </>
        );
      }
    },
    {
      id: 'supplierName',
      accessorKey: 'supplierName',
      header: 'Supplier',
      enableSorting: false,
      cell: ({ row: { original } }) =>
        original.isPending || !original.supplierId ? (
          <span className="text-body-tertiary">Pending supplier</span>
        ) : (
          <SupplierName
            compact
            supplierId={original.supplierId}
            supplierName={original.supplierName}
          />
        )
    },
    {
      id: 'createdOn',
      accessorKey: 'createdOn',
      header: 'Created',
      cell: ({ row: { original } }) => (
        <span>{formatLongIsoDateOrDash(original.createdOn)}</span>
      )
    },
    {
      id: 'sentOn',
      accessorKey: 'sentOn',
      header: 'Sent',
      cell: ({ row: { original } }) => (
        <span>{formatLongIsoDateOrDash(original.sentOn)}</span>
      )
    },
    {
      id: 'closedOn',
      accessorKey: 'closedOn',
      header: 'Closed',
      cell: ({ row: { original } }) => (
        <span>{formatLongIsoDateOrDash(original.closedOn)}</span>
      )
    },
    {
      id: 'estimatedProcurementAmount',
      accessorKey: 'estimatedProcurementAmount',
      header: 'Est. Amount',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const amount = original.estimatedProcurementAmount;
        if (amount == null) {
          return <span className="text-body-tertiary">—</span>;
        }
        return (
          <span>
            ₱{formatNumberWithCommas(amount)}
            {original.hasIncompleteEstimate && (
              <span className="text-body-tertiary fs-10 ms-1">partial</span>
            )}
          </span>
        );
      }
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const isAnalyzing = original.status === 'Analyzing';
        return (
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <BadgeStatus status={original.status} />
            {original.needsConsolidation && (
              <BadgeStatus status="ForConsolidation" />
            )}
            {(original.hasBackgroundTaskInProgress || isAnalyzing) && (
              <ProgressBadge
                label={isAnalyzing ? 'Analyzing…' : 'In progress'}
                title={
                  isAnalyzing
                    ? 'AI ordering analysis in progress'
                    : 'Background task in progress'
                }
              />
            )}
          </div>
        );
      }
    },
    {
      header: 'Delete',
      enableSorting: false,
      cell: ({ row: { original } }) => {
        const showRequestDeletion = canRequestDeletion(original.status);
        const showHardDelete =
          canDelete && original.status === 'ForDeletion';

        if (!showRequestDeletion && !showHardDelete) {
          return null;
        }

        return (
          <FontAwesomeIcon
            icon={faTrash}
            style={{ cursor: 'pointer', color: '#C63C51' }}
            title={
              showHardDelete
                ? 'Delete purchase order'
                : 'Request for deletion'
            }
            onClick={() =>
              handleDeletePurchaseOrder(
                original.purchaseOrderId,
                original.status
              )
            }
          />
        );
      }
    }
  ];

  const handleSubmit = async () => {
    if (addPurchaseOrderFormRef.current) {
      try {
        const isSuccess = await addPurchaseOrderFormRef.current.submit();
        if (isSuccess) {
          setIsSubmitted(true);
          modalRef.current.hide();
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleDeletePurchaseOrder = async (
    purchaseOrderId: number,
    status: string
  ) => {
    try {
      if (status === 'ForDeletion') {
        if (!canDelete) {
          toast.error('Only an admin can delete this purchase order.', {
            ...toastConfig,
            autoClose: 3000
          });
          return;
        }

        await axiosInstance.delete(
          `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}`
        );
        toast.success('Purchase order deleted', {
          ...toastConfig,
          autoClose: 2000
        });
      } else {
        await axiosInstance.put(
          `/api/v1/${selectedBranch?.branchDomain}/purchaseOrders/${purchaseOrderId}/requestDeletion`
        );
        toast.success('Request for deletion sent', {
          ...toastConfig,
          autoClose: 2000
        });
      }
      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleCreatePurchaseOrder = () => {
    setModalProps({
      title: 'Create Purchase Order',
      content: <CreatePurchaseOrderForm ref={addPurchaseOrderFormRef} />,
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleSubmit()
    });
    modalRef.current.show();
  };

  const selectedSupplierId =
    filters.supplierId != null && String(filters.supplierId) !== ''
      ? Number(filters.supplierId)
      : undefined;

  const handleCloseOldPurchaseOrders = async () => {
    if (!selectedBranch?.branchDomain || isClosingOld) {
      return;
    }

    setIsClosingOld(true);
    try {
      const { data } = await axiosInstance.put<{ closedCount: number }>(
        `/api/v1/${selectedBranch.branchDomain}/purchaseOrders/closeOld`,
        null,
        {
          params: selectedSupplierId ? { supplierId: selectedSupplierId } : undefined
        }
      );
      modalRef.current.hide();
      toast.success(
        data.closedCount === 0
          ? 'No old purchase orders to close'
          : `Closed ${data.closedCount} old purchase order${data.closedCount === 1 ? '' : 's'}`,
        { ...toastConfig, autoClose: 3000 }
      );
      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    } finally {
      setIsClosingOld(false);
    }
  };

  const handleCombinePurchaseOrders = async () => {
    const selected = Array.from(selectedOrdersRef.current.values());
    if (!selectedBranch?.branchDomain || isCombining || selected.length < 2) {
      return;
    }

    setIsCombining(true);
    try {
      const { data } = await axiosInstance.post<{
        targetPurchaseOrderId: number;
        combinedPurchaseOrderCount: number;
        movedItemCount: number;
      }>(`/api/v1/${selectedBranch.branchDomain}/purchaseOrders/combine`, {
        purchaseOrderIds: selected.map(order => order.purchaseOrderId)
      });
      modalRef.current.hide();
      setSelectedOrders(new Map());
      toast.success(
        `Combined ${data.combinedPurchaseOrderCount} purchase orders into #${data.targetPurchaseOrderId}`,
        { ...toastConfig, autoClose: 3000 }
      );
      setIsSubmitted(true);
      navigate(`/purchase-order-details/${data.targetPurchaseOrderId}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage ||
            'Failed to combine purchase orders',
          { ...toastConfig, autoClose: 3000 }
        );
      }
    } finally {
      setIsCombining(false);
    }
  };

  const confirmCombinePurchaseOrders = () => {
    const selected = Array.from(selectedOrders.values());
    if (selected.length < 2) {
      toast.error('Select at least two purchase orders to combine', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    if (selected.some(order => !canCombinePurchaseOrder(order))) {
      toast.error(
        'Only new, future, awaiting approval, or approved purchase orders can be combined',
        { ...toastConfig, autoClose: 3000 }
      );
      return;
    }

    const supplierIds = new Set(selected.map(order => order.supplierId));
    if (supplierIds.size !== 1) {
      toast.error('Combine purchase orders from the same supplier', {
        ...toastConfig,
        autoClose: 3000
      });
      return;
    }

    const supplierName = selected[0].supplierName;
    const ids = selected
      .map(order => `#${order.purchaseOrderId}`)
      .join(', ');
    setModalProps({
      title: 'Combine purchase orders',
      content: (
        <p>
          Combine {selected.length} purchase orders from {supplierName} (
          {ids}) into the oldest order? Open items will be added there. Empty
          source orders will be cancelled.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleCombinePurchaseOrders(),
      onSubmitCustomDisplay: isCombining ? 'Combining…' : 'Combine'
    });
    modalRef.current.show();
  };

  const confirmCloseOldPurchaseOrders = () => {
    const scope = selectedSupplierId
      ? 'the selected supplier'
      : 'each supplier';
    setModalProps({
      title: 'Close old purchase orders',
      content: (
        <p>
          Close every purchase order that is not among the 3 latest existing
          orders for {scope}? Newer orders stay as they are.
        </p>
      ),
      onCancel: () => modalRef.current.hide(),
      onSubmit: () => handleCloseOldPurchaseOrders(),
      onSubmitCustomDisplay: 'Close old orders'
    });
    modalRef.current.show();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterChange = (key: string, value: any) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [key]: value
    }));
  };

  useEffect(() => {
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  return (
    <>
      <Row lg="1" className="mb-3">
        <h4>Purchase Order</h4>
      </Row>
      {isAdmin && (
        <ProcurementLimitWarning
          branchDomain={selectedBranch?.branchDomain}
          variant="purchaseOrder"
        />
      )}
      <DataTable
        queryKey={[
          'purchaseOrder',
          selectedBranch?.branchDomain,
          isSubmitted || filters
        ]}
        queryFn={fetchData}
        enabled={Boolean(selectedBranch?.branchDomain)}
        columns={columns}
        rightSideActions={
          <>
            {suppliers && (
              <Form.Select
                style={{
                  height: '2.4rem',
                  width: '20%'
                }}
                value={filters.supplierId}
                onChange={e => handleFilterChange('supplierId', e.target.value)}
              >
                <option value="">All Supplier</option>
                {suppliers?.map(data => (
                  <option key={data.supplierId} value={data.supplierId}>
                    {data.name}
                  </option>
                ))}
              </Form.Select>
            )}
            {purchaseOrderStatus && (
              <Form.Select
                style={{
                  height: '2.4rem',
                  width: '20%'
                }}
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
              >
                <option value="">Open</option>
                {purchaseOrderStatus?.map((option, index) => (
                  <option value={option.value} key={index}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            )}
            <Row lg="auto" className="g-2">
              <Link
                to="/purchase-order/pending"
                className="btn btn-phoenix-secondary w-10 mb-3"
              >
                Pending list
              </Link>
              <Link
                to="/resupply"
                className="btn btn-phoenix-secondary w-10 mb-3"
              >
                Resupply
              </Link>
              {canCloseOld && (
                <Button
                  variant="phoenix-secondary"
                  className="w-10 mb-3"
                  disabled={isClosingOld}
                  onClick={confirmCloseOldPurchaseOrders}
                >
                  {isClosingOld ? 'Closing…' : 'Close old orders'}
                </Button>
              )}
              <Button
                variant="phoenix-secondary"
                className="w-10 mb-3"
                disabled={selectedOrders.size < 2 || isCombining}
                onClick={confirmCombinePurchaseOrders}
              >
                {isCombining
                  ? 'Combining…'
                  : `Combine${
                      selectedOrders.size > 0
                        ? ` (${selectedOrders.size})`
                        : ''
                    }`}
              </Button>
              <Button
                variant="primary"
                className="w-10 mb-3 me-5"
                onClick={handleCreatePurchaseOrder}
              >
                Create Purchase Order
              </Button>
            </Row>
          </>
        }
      />
      <CustomModal
        ref={modalRef}
        title={modalProps?.title}
        content={modalProps?.content}
        onCancel={modalProps?.onCancel}
        onSubmit={modalProps?.onSubmit}
        onSubmitCustomDisplay={modalProps?.onSubmitCustomDisplay}
      />
    </>
  );
};

export default PurchaseOrder;
